/**
 * SessionChoiceModal Component
 * Modal dialog asking user if they want to continue previous session or start new
 */

import { X } from 'lucide-react';
import { useEffect } from 'react';
import type { Language } from './VoiceOrderingContainer';

interface SessionChoiceModalProps {
  isOpen: boolean;
  language: Language;
  onContinue: () => void;
  onStartNew: () => void;
}

export function SessionChoiceModal({
  isOpen,
  language,
  onContinue,
  onStartNew,
}: SessionChoiceModalProps) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      // Clicked backdrop - default to "Start New"
      onStartNew();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{
        background: 'rgba(0, 0, 0, 0.5)',
      }}
      onClick={handleBackdropClick}
    >
      <div
        className="relative flex flex-col rounded-2xl p-6 shadow-2xl"
        style={{
          width: '90%',
          maxWidth: '400px',
          background: 'var(--card-bg)',
          direction: language === 'ar' ? 'rtl' : 'ltr',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onStartNew}
          className="absolute right-4 top-4 flex items-center justify-center rounded-full p-1.5 transition-all hover:bg-opacity-80"
          style={{
            background: 'var(--border)',
            color: 'var(--text-secondary)',
          }}
          aria-label={language === 'en' ? 'Close' : 'إغلاق'}
        >
          <X className="h-4 w-4" />
        </button>

        {/* Content */}
        <div className="mt-2 space-y-4">
          {/* Title */}
          <h2
            className="text-xl font-bold"
            style={{ color: 'var(--text-primary)' }}
          >
            {language === 'en'
              ? 'Continue Previous Session?'
              : 'هل تريد متابعة الجلسة السابقة؟'}
          </h2>

          {/* Description */}
          <p
            className="text-sm leading-relaxed"
            style={{ color: 'var(--text-secondary)' }}
          >
            {language === 'en'
              ? 'We found a previous session with your conversation history. Would you like to continue where you left off, or start a new session?'
              : 'وجدنا جلسة سابقة مع تاريخ محادثتك. هل تريد متابعة من حيث توقفت، أم بدء جلسة جديدة؟'}
          </p>

          {/* Buttons */}
          <div className="flex flex-col gap-3 pt-2">
            {/* Continue Button (Primary) */}
            <button
              onClick={onContinue}
              className="w-full rounded-lg py-3 px-4 font-medium transition-all active:scale-95"
              style={{
                background: 'var(--accent-gold)',
                color: 'white',
              }}
            >
              {language === 'en' ? 'Continue' : 'متابعة'}
            </button>

            {/* Start New Button (Secondary) */}
            <button
              onClick={onStartNew}
              className="w-full rounded-lg border py-3 px-4 font-medium transition-all active:scale-95"
              style={{
                background: 'transparent',
                color: 'var(--text-primary)',
                borderColor: 'var(--border)',
              }}
            >
              {language === 'en' ? 'Start New' : 'بدء جديد'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
